import {
    getStringValue,
    isRecord,
} from '@apps/web/components/phoebe_chat/record_utils';
import {
    type RunTraceEntry,
    type SaveNoteArtifact,
    type SaveNoteArtifactEntry,
    type ToolExecutionEntry,
} from '@apps/web/components/phoebe_chat_view/transcript_types';

function getSaveNoteArtifact(
    argumentsValue: unknown,
    outputValue: unknown,
): SaveNoteArtifact | null {
    const args = isRecord(argumentsValue) ? argumentsValue : null;
    const output = isRecord(outputValue) ? outputValue : null;

    const content =
        args && typeof args['content'] === 'string'
            ? args['content'].trim()
            : '';
    // A failed save_note call completes with no note_id in its output;
    // rendering a card for it would present the error as an in-progress
    // save, so only successful saves produce an artifact.
    const noteId =
        output && typeof output['note_id'] === 'string'
            ? output['note_id']
            : null;
    if (!content || !noteId) {
        return null;
    }
    return { noteId, content };
}

function formatArtifactLabel(value: string | null): string | null {
    if (!value) {
        return null;
    }
    return value
        .trim()
        .toLowerCase()
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/\b\w/gu, (char) => char.toUpperCase());
}

function getTrimmedStringValue(
    record: Record<string, unknown> | null,
    key: string,
): string | null {
    return getStringValue(record, key)?.trim() ?? null;
}

function getOutreachFilterRuleArtifact(
    argumentsValue: unknown,
    outputValue: unknown,
): SaveNoteArtifact | null {
    const args = isRecord(argumentsValue) ? argumentsValue : null;
    const output = isRecord(outputValue) ? outputValue : null;
    const status = getTrimmedStringValue(output, 'status');
    if (
        status !== 'added' &&
        status !== 'updated' &&
        status !== 'already_exists'
    ) {
        return null;
    }

    const ruleId = getTrimmedStringValue(output, 'rule_id');
    if (!ruleId) {
        return null;
    }

    const outreachType = formatArtifactLabel(
        getTrimmedStringValue(output, 'outreach_type') ??
            getTrimmedStringValue(args, 'outreach_type'),
    );
    const entityType = formatArtifactLabel(
        getTrimmedStringValue(output, 'entity_type') ??
            getTrimmedStringValue(args, 'entity_type'),
    );
    const filterMode = formatArtifactLabel(
        getTrimmedStringValue(output, 'filter_mode') ??
            getTrimmedStringValue(args, 'filter_mode'),
    );
    const ruleScope = [
        outreachType,
        entityType?.toLowerCase(),
        filterMode?.toLowerCase(),
    ]
        .filter((value): value is string => value !== null)
        .join(' ');
    const content = ruleScope ? `${ruleScope} rule` : 'Outreach filter rule';

    return {
        noteId: ruleId,
        title: 'Phoebe saved a rule',
        content,
    };
}

function getArtifactForCompletedTool({
    toolName,
    argumentsValue,
    outputValue,
}: {
    readonly toolName: string;
    readonly argumentsValue: unknown;
    readonly outputValue: unknown;
}): SaveNoteArtifact | null {
    switch (toolName) {
        case 'save_note':
            return getSaveNoteArtifact(argumentsValue, outputValue);
        case 'update_outreach_filter_rule':
            return getOutreachFilterRuleArtifact(argumentsValue, outputValue);
        default:
            return null;
    }
}

export function collectSaveNoteArtifacts(
    entries: Array<RunTraceEntry>,
): Array<SaveNoteArtifactEntry> {
    const toolEntries: Array<ToolExecutionEntry> = [];

    for (const entry of entries) {
        if (entry.kind === 'tool-execution') {
            toolEntries.push(entry);
            continue;
        }
        if (entry.kind === 'thought') {
            toolEntries.push(...entry.tools);
        }
    }

    const artifacts: Array<SaveNoteArtifactEntry> = [];
    let previousArtifactToolIndex = -2;
    let staggerIndex = 0;
    toolEntries.forEach((toolEntry, index) => {
        if (!toolEntry.isComplete) {
            return;
        }
        const artifact = getArtifactForCompletedTool({
            toolName: toolEntry.name,
            argumentsValue: toolEntry.arguments,
            outputValue: toolEntry.output,
        });
        if (!artifact) {
            return;
        }
        // Back-to-back saves form a burst whose chips animate in one at a
        // time; any interleaved tool resets the cadence.
        staggerIndex =
            index === previousArtifactToolIndex + 1 ? staggerIndex + 1 : 0;
        previousArtifactToolIndex = index;
        artifacts.push({
            kind: 'save-note-artifact' as const,
            id: `save-note-artifact-${toolEntry.id}`,
            createdAt: toolEntry.createdAt,
            artifact,
            turnSequence: toolEntry.turnSequence,
            staggerIndex,
        });
    });
    return artifacts;
}
